'use client';

import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import MarcoEscalado from '@/components/marketing/comun/MarcoEscalado';
import { PANTALLA_INICIAL, PANTALLAS_APP, type PantallaApp } from '@/content/pantallasApp';
import { cn } from '@/lib/utils';

import { TEXTOS_SECCION_MOCKUPS as TEXTOS } from './textosSeccionMockups';

interface PropiedadesPestanasPantallas {
    /** Los siete mockups de escritorio ya pintados en el servidor, en el orden de PANTALLAS_APP. */
    paneles: ReactNode[];
    /** El CTA, también de servidor: va bajo la lista, alineado con el pie del panel. */
    cta: ReactNode;
}

const ID_PANEL = 'pantalla-panel';
const TOTAL = PANTALLAS_APP.length;

function idPestana(pantalla: PantallaApp): string {
    return `pantalla-tab-${pantalla.numero}`;
}

/** El destino de cada tecla de la lista vertical; las flechas dan la vuelta (UI-SPEC §Interaction 3). */
function destinoDeTecla(tecla: string, indice: number): number | null {
    const saltos: Record<string, number> = { ArrowDown: indice + 1, ArrowUp: indice - 1, Home: 0, End: TOTAL - 1 };
    const destino = saltos[tecla];
    return destino === undefined ? null : (destino + TOTAL) % TOTAL;
}

interface PropiedadesPestana {
    pantalla: PantallaApp;
    elegida: boolean;
    alElegir: () => void;
    alPulsarTecla: (evento: KeyboardEvent<HTMLButtonElement>) => void;
    refBoton: (nodo: HTMLButtonElement | null) => void;
}

function Pestana({ pantalla, elegida, alElegir, alPulsarTecla, refBoton }: PropiedadesPestana) {
    return (
        <button
            ref={refBoton}
            type="button"
            role="tab"
            id={idPestana(pantalla)}
            aria-selected={elegida}
            aria-controls={ID_PANEL}
            tabIndex={elegida ? 0 : -1}
            onClick={alElegir}
            onKeyDown={alPulsarTecla}
            className={cn(
                'flex w-full items-start gap-3 rounded-[14px] border-2 px-[14px] py-2 text-left font-sans text-ink',
                elegida ? 'border-ink bg-surface-2 shadow-ds-2' : 'border-transparent bg-transparent hover:border-ink/40',
            )}
        >
            <span
                className={cn(
                    'flex size-[26px] shrink-0 items-center justify-center rounded-ds-sm text-[13px] font-bold',
                    elegida ? 'bg-accent-blue text-on-accent-blue' : 'bg-surface-1 text-ink',
                )}
            >
                {pantalla.numero}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
                <strong data-titulo className="font-display text-[16px] leading-[1.25]">
                    {pantalla.titulo}
                </strong>
                <span className={cn('text-[13px] leading-[1.4]', elegida ? 'text-ink/90' : 'text-on-surface-variant')}>
                    {pantalla.frase}
                </span>
            </span>
        </button>
    );
}

function NotaFijate({ texto }: { texto: string }) {
    return (
        <div className="mx-[18px] flex items-start gap-3 rounded-[14px] border-2 border-ink bg-surface-2 px-4 py-3 shadow-ds-2">
            <span className="flex h-6 shrink-0 items-center rounded-full border-2 border-ink bg-accent-blue px-[10px] text-[12px] font-bold text-on-accent-blue">
                {TEXTOS.etiquetaFijate}
            </span>
            <span data-fijate className="text-[15px] leading-[1.5] text-ink">
                {texto}
            </span>
        </div>
    );
}

/**
 * Las pestañas de escritorio de Seccion_Mockups (UI-SPEC §Interaction 3): tabindex itinerante,
 * flechas con vuelta, Inicio/Fin y activación automática. El estado arranca en PANTALLA_INICIAL y
 * nunca se copia de las props.
 */
export default function PestanasPantallas({ paneles, cta }: PropiedadesPestanasPantallas) {
    const [elegida, setElegida] = useState(PANTALLA_INICIAL - 1);
    const botones = useRef<(HTMLButtonElement | null)[]>([]);

    const alPulsarTecla = (evento: KeyboardEvent<HTMLButtonElement>, indice: number) => {
        const destino = destinoDeTecla(evento.key, indice);
        if (destino === null) return;
        evento.preventDefault();
        setElegida(destino);
        botones.current[destino]?.focus();
    };

    // Por debajo de 1440 la lista se estrecha a 280 px antes que encoger más el mockup, cuyo texto
    // ya baja de 8 px (UI-SPEC, fila de respaldo «fixed-geometry»).
    return (
        <div className="mt-[72px] grid grid-cols-[280px_minmax(0,1fr)] gap-6 min-[1440px]:grid-cols-[344px_minmax(0,902px)] min-[1440px]:gap-[34px]">
            <div className="flex flex-col justify-between gap-ds-6">
                <div role="tablist" aria-label={TEXTOS.etiquetaLista} aria-orientation="vertical" className="flex flex-col gap-1">
                    {PANTALLAS_APP.map((pantalla, indice) => (
                        <Pestana
                            key={pantalla.numero}
                            pantalla={pantalla}
                            elegida={indice === elegida}
                            alElegir={() => setElegida(indice)}
                            alPulsarTecla={(evento) => alPulsarTecla(evento, indice)}
                            refBoton={(nodo) => {
                                botones.current[indice] = nodo;
                            }}
                        />
                    ))}
                </div>
                <div>{cta}</div>
            </div>
            <PanelPantallas paneles={paneles} elegida={elegida} />
        </div>
    );
}

/**
 * Solo hay un MarcoEscalado para los siete paneles: si cada panel tuviera el suyo, el que estaba
 * oculto mediría ancho cero al mostrarse. Los no elegidos siguen en el DOM con `hidden`.
 * El relleno inferior de 14 px deja el pie del CTA 14 px por debajo del panel, como en el tablero.
 */
function PanelPantallas({ paneles, elegida }: { paneles: ReactNode[]; elegida: number }) {
    return (
        <div role="tabpanel" id={ID_PANEL} aria-labelledby={idPestana(PANTALLAS_APP[elegida])} className="flex flex-col gap-[14px] pb-[14px]">
            <div className="w-full max-w-[902px]">
                <MarcoEscalado anchoDiseno={1504} altoDiseno={968} escalaInicial={0.6}>
                    {paneles.map((panel, indice) => (
                        <div key={PANTALLAS_APP[indice]?.numero ?? indice} data-panel hidden={indice !== elegida}>
                            {panel}
                        </div>
                    ))}
                </MarcoEscalado>
            </div>
            <NotaFijate texto={PANTALLAS_APP[elegida].fijate} />
            <div className="mx-[18px]">
                <LeyendaIlustracion texto={TEXTOS.leyenda} />
            </div>
        </div>
    );
}
